import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../lib/utils';
import { toast } from 'sonner';
import { CreditCard, Loader2, ShoppingBag, HelpCircle, X } from 'lucide-react';

export const Checkout = () => {
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [loading, setLoading] = useState(false);
  const { cart, getCartTotal } = useCart();
  const { user } = useAuth();
  const [showShippingInfo, setShowShippingInfo] = useState(false);
  const [showTaxInfo, setShowTaxInfo] = useState(false);

  // Contact and Delivery Form State
  const [formData, setFormData] = useState({
    email: '',
    emailUpdates: false,
    country: 'United States',
    firstName: '',
    lastName: '',
    company: '',
    address: '',
    apartment: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    textUpdates: false
  });

  const cartItems = cart?.items || cart || [];
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCheckout = async () => {
    if (!cartItems || cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);

    try {
      const origin = window.location.origin;

      if (paymentMethod === 'card' || paymentMethod === 'stripe') {
        // If user is logged in, use authenticated checkout
        if (user) {
          const token = localStorage.getItem('token');
          const response = await axios.post(
            `${API_URL}/payments/stripe/checkout`,
            {
              success_url: `${origin}/order-success?session_id={CHECKOUT_SESSION_ID}`,
              cancel_url: `${origin}/checkout`
            },
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          window.location.href = response.data.url;
        } else {
          // Guest checkout - no authentication required
          const response = await axios.post(
            `${API_URL}/payments/stripe/guest-checkout`,
            {
              cart_items: cartItems,
              email: formData.email,
              success_url: `${origin}/order-success?session_id={CHECKOUT_SESSION_ID}`,
              cancel_url: `${origin}/checkout`
            }
          );
          window.location.href = response.data.url;
        }
      } else if (paymentMethod === 'paypal') {
        toast.info('PayPal integration coming soon!');
        setLoading(false);
      } else if (paymentMethod === 'applepay') {
        toast.info('Apple Pay integration coming soon!');
        setLoading(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed. Please try again.');
      setLoading(false);
    }
  };

  const cartTotal = getCartTotal();
  const subtotal = cartTotal;
  const total = subtotal;

  return (
    <div className="min-h-screen bg-purple-25 py-12 px-6 lg:px-12">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-light">Checkout</h1>
          {!user && (
            <div className="text-sm text-stone-600">
              Already a member?{' '}
              <button
                onClick={() => navigate('/login?redirect=/checkout')}
                className="text-purple-600 hover:text-purple-700 underline font-medium"
              >
                Login here
              </button>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Contact and Delivery Form */}
          <div className="md:col-span-2">
            
            {/* Contact Section */}
            <div className="bg-white rounded-2xl p-8 mb-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-heading font-light">Contact</h2>
                {!user && (
                  <button
                    onClick={() => navigate('/login?redirect=/checkout')}
                    className="text-purple-600 hover:text-purple-700 underline text-sm"
                  >
                    Sign in
                  </button>
                )}
              </div>
              
              <div className="space-y-4">
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="emailUpdates"
                    id="emailUpdates"
                    checked={formData.emailUpdates}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="emailUpdates" className="text-sm text-stone-600">
                    Email me with news and offers
                  </label>
                </div>
              </div>
            </div>

            {/* Delivery Section */}
            <div className="bg-white rounded-2xl p-8 mb-6">
              <h2 className="text-2xl font-heading font-light mb-6">Delivery</h2>
              
              <div className="space-y-4">
                {/* Country */}
                <div>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option>United States</option>
                  </select>
                </div>

                {/* First and Last Name */}
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First name"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Last name"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>

                {/* Company */}
                <div>
                  <input
                    type="text"
                    name="company"
                    placeholder="Company (optional)"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* Address */}
                <div>
                  <input
                    type="text"
                    name="address"
                    placeholder="Address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>

                {/* Apartment */}
                <div>
                  <input
                    type="text"
                    name="apartment"
                    placeholder="Apartment, suite, etc. (optional)"
                    value={formData.apartment}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                {/* City, State, ZIP */}
                <div className="grid grid-cols-3 gap-4">
                  <input
                    type="text"
                    name="city"
                    placeholder="City"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    className="px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    <option value="">State</option>
                    <option value="NY">NY</option>
                    <option value="CA">CA</option>
                    <option value="TX">TX</option>
                    <option value="FL">FL</option>
                    <option value="IL">IL</option>
                  </select>
                  <input
                    type="text"
                    name="zipCode"
                    placeholder="ZIP code"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    className="px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>

                {/* Phone */}
                <div>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="textUpdates"
                    id="textUpdates"
                    checked={formData.textUpdates}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="textUpdates" className="text-sm text-stone-600">
                    Text me with news and offers
                  </label>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="bg-white rounded-2xl p-8 mb-6">
              <h2 className="text-2xl font-heading font-light mb-6">Order Summary</h2>
              
              {cartItems && cartItems.length > 0 ? (
                <div className="space-y-4">
                  {cartItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-4 bg-purple-50 rounded-xl">
                      {item.image_url && (
                        <div className="w-20 h-20 bg-white rounded-lg flex items-center justify-center p-2 flex-shrink-0">
                          <img
                            src={item.image_url}
                            alt={item.product_name}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="font-medium">{item.product_name}</h3>
                        <p className="text-sm text-stone-600">Size: {item.size}</p>
                        <p className="text-sm text-stone-600">Quantity: {item.quantity}</p>
                      </div>
                      <p className="text-lg font-semibold text-purple-900">
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ShoppingBag className="h-16 w-16 mx-auto mb-4 text-stone-400" />
                  <p className="text-stone-600">Your cart is empty</p>
                </div>
              )}
            </div>

            {/* Payment Section */}
            <div className="bg-white rounded-2xl p-8">
              <h2 className="text-2xl font-heading font-light mb-2">Payment</h2>
              <p className="text-sm text-stone-600 mb-6">All transactions are secure and encrypted.</p>
              
              <div className="space-y-4">
                {/* Credit Card Option */}
                <div className={`border-2 rounded-lg ${paymentMethod === 'card' ? 'border-stone-900' : 'border-stone-300'}`}>
                  <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => setPaymentMethod('card')}>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'card' ? 'border-stone-900' : 'border-stone-400'}`}>
                      {paymentMethod === 'card' && <div className="w-3 h-3 rounded-full bg-stone-900"></div>}
                    </div>
                    <label className="flex-1 cursor-pointer font-medium">Credit card</label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-stone-600">💳 Visa Mastercard Amex +4</span>
                    </div>
                  </div>
                  
                  {paymentMethod === 'card' && (
                    <div className="p-4 pt-0 space-y-4 bg-stone-50">
                      <div>
                        <input
                          type="text"
                          placeholder="Card number"
                          className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Expiration date (MM / YY)"
                          className="px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                        <input
                          type="text"
                          placeholder="Security code"
                          className="px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          placeholder="Name on card"
                          className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="useBillingAddress"
                          defaultChecked
                          className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                        />
                        <label htmlFor="useBillingAddress" className="text-sm text-stone-700">
                          Use shipping address as billing address
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {/* PayPal Option */}
                <div className={`border-2 rounded-lg ${paymentMethod === 'paypal' ? 'border-stone-900' : 'border-stone-300'}`}>
                  <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => setPaymentMethod('paypal')}>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'paypal' ? 'border-stone-900' : 'border-stone-400'}`}>
                      {paymentMethod === 'paypal' && <div className="w-3 h-3 rounded-full bg-stone-900"></div>}
                    </div>
                    <label className="flex-1 cursor-pointer font-medium">PayPal</label>
                    <span className="text-blue-600 font-semibold">PayPal</span>
                  </div>
                </div>

                {/* Apple Pay Option */}
                <div className={`border-2 rounded-lg ${paymentMethod === 'applepay' ? 'border-stone-900' : 'border-stone-300'}`}>
                  <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => setPaymentMethod('applepay')}>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'applepay' ? 'border-stone-900' : 'border-stone-400'}`}>
                      {paymentMethod === 'applepay' && <div className="w-3 h-3 rounded-full bg-stone-900"></div>}
                    </div>
                    <label className="flex-1 cursor-pointer font-medium">Apple Pay</label>
                    <div className="px-3 py-1 border-2 border-stone-900 rounded text-sm font-medium">
                       Pay
                    </div>
                  </div>
                </div>
              </div>

              {/* Remember Me Section */}
              <div className="mt-8 border-t border-stone-200 pt-6">
                <h3 className="font-medium mb-4">Remember me</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="saveInfo"
                      defaultChecked
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <label htmlFor="saveInfo" className="text-sm text-stone-700">
                      Save my information for a faster checkout
                    </label>
                  </div>
                  <div>
                    <label className="text-sm text-stone-600 block mb-2">
                      Mobile phone number
                    </label>
                    <div className="flex gap-2">
                      <span className="px-3 py-2 border border-stone-300 rounded bg-stone-50 text-sm">+1</span>
                      <input
                        type="tel"
                        placeholder=""
                        className="flex-1 px-4 py-2 border border-stone-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-sm text-stone-500 mt-6 flex items-center gap-2 justify-center">
                <span>🔒</span>
                Secure and encrypted
              </p>
            </div>
          </div>

          {/* Price Summary */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-2xl p-8 sticky top-6">
              <h3 className="text-xl font-heading font-light mb-6">Price Details</h3>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-stone-600">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-stone-600">
                  <div className="flex items-center gap-1">
                    <span>Shipping</span>
                    <button 
                      onClick={() => setShowShippingInfo(true)}
                      className="text-stone-400 hover:text-stone-600"
                    >
                      <HelpCircle className="w-4 h-4" />
                    </button>
                  </div>
                  {!formData.address ? (
                    <span className="text-xs text-stone-400">Enter shipping address</span>
                  ) : (
                    <span>-</span>
                  )}
                </div>
                <div className="flex justify-between text-stone-600">
                  <div className="flex items-center gap-1">
                    <span>Tax</span>
                    <button 
                      onClick={() => setShowTaxInfo(true)}
                      className="text-stone-400 hover:text-stone-600"
                    >
                      <HelpCircle className="w-4 h-4" />
                    </button>
                  </div>
                  <span>-</span>
                </div>
                <div className="border-t border-stone-200 pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold">Total</span>
                    <span className="text-2xl font-bold text-purple-900">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleCheckout}
                disabled={loading || !cartItems || cartItems.length === 0 || !formData.email || !formData.address || !formData.city || !formData.state || !formData.zipCode}
                className="w-full rounded-lg bg-stone-900 hover:bg-stone-800 text-white h-14 text-lg font-medium"
                size="lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Pay now
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-stone-500 mt-4">
                By continuing, you agree to our Terms & Conditions
              </p>
            </div>
          </div>
        </div>

        {/* Shipping Info Modal */}
        {showShippingInfo && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">Processing & Delivery</h3>
                <button onClick={() => setShowShippingInfo(false)} className="text-stone-500 hover:text-stone-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marahhealth/artifacts/evjoxelh_image.png" 
                  alt="Processing & Delivery Information" 
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        )}

        {/* Tax Info Modal */}
        {showTaxInfo && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">Tax Information</h3>
                <button onClick={() => setShowTaxInfo(false)} className="text-stone-500 hover:text-stone-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_marahhealth/artifacts/6ceskmuu_image.png" 
                  alt="Tax Information" 
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
